import React, { FC } from 'react';
import { Box, Text } from '@bechtel/gds';
import { CommodityStatusCode, PersonaType, CommodityAssignee, Block, BlockStatusCode } from '@bechtel/types';
import { EllipseStyled, WeldStatusStyled } from './styled';
import {
  TextWithEllipsis,
  useDate,
  RestrictTo,
  useConfig,
  useAppUser,
  EllipsisPlacement,
} from '@bechtel/client-common';
import { t } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { WeldStatusMessages, WeldAvatarGroup } from '@bechtel/client-weld-common';
import { parseISO } from 'date-fns';
import { PriorityWeldIcon } from '~/components/atoms';
import { filter, flatMap, some } from 'lodash';
import { WeldItemCardProps } from './types';

export const WeldItemCard: FC<WeldItemCardProps> = ({ weld }) => {
  const { user } = useAppUser();
  const { i18n } = useLingui();
  const { formatRelativeInWords } = useDate();
  const { dateFormat } = useConfig();
  const isUserForeman = user?.crewMember.persona === PersonaType.FOREMAN;
  const weldOwners = filter(weld?.commodityAssignees, (item: CommodityAssignee) => {
    return item.personaType === PersonaType.FOREMAN || item.personaType === PersonaType.FIELD_WELD_ENGINEER;
  });
  const hasWeldPipeDia = weld.pipeDiameter !== undefined && weld.pipeDiameter !== null && weld.pipeDiameter > 0;

  const blocks = flatMap(weld.milestones,'blocks');
  const hasOpenBlocks = some(blocks, (item:Block) =>  item.currentStatusI18n == BlockStatusCode.BLOCK_OPENED)
  const weldStatus = (hasOpenBlocks) ? CommodityStatusCode.BLOCKED : weld.status?.status!
  return (
    <Box data-testid="weld-item-container">
      <WeldStatusStyled status={weldStatus} />
      <Box flexDirection={{ sm: 'column' }} flexGrow={{ sm: 1 }} style={{ overflow: 'hidden' }}>
        {weld.status?.status && (
          <Box mb={{ sm: '100' }}>
            <Text style={{ fontStyle: 'italic' }} color="grey.700" size="200" data-testid="weld-status">
              {i18n._(WeldStatusMessages[weldStatus])}
            </Text>
          </Box>
        )}
        <TextWithEllipsis
          ellipsisPlacement={EllipsisPlacement.END}
          data-testid="weld-tag"
          size="400"
          color={weldStatus == CommodityStatusCode.BLOCKED ? 'red.700' : 'grey.1000'}
          weight="600"
          transform="uppercase"
        >
          {weld.tag}
        </TextWithEllipsis>
        <Box mb={{ sm: '450' }} data-testid="weld-loc-desc">
          <TextWithEllipsis
            ellipsisPlacement={EllipsisPlacement.END}
            size="300"
            color={weldStatus == CommodityStatusCode.BLOCKED ? 'red.700' : 'grey.700'}
          >
            {weld.locationDescription}
          </TextWithEllipsis>
        </Box>
        <Box alignItems={{ sm: 'center' }}>
          {weld.status?.statusAt && (
            <Box mr={{ sm: '200' }}>
              <Text size="300" color="grey.700" family="graphik" style={{ opacity: '0.7' }}>
                {formatRelativeInWords(
                  parseISO(weld.status?.statusAt),
                  new Date(),
                  RestrictTo.Day,
                  dateFormat,
                  ``,
                  false
                )}
              </Text>
            </Box>
          )}
          {weld.status?.status && (weld.pipeMaterial || hasWeldPipeDia) && (
            <Box mr={{ sm: '200' }}>
              <Text family="graphik" size="500" color="grey.700">
                /
              </Text>
            </Box>
          )}
          {weld.pipeMaterial && (
            <Text size="300" color="grey.700">
              {weld.pipeMaterial}
            </Text>
          )}
          {weld.pipeMaterial && hasWeldPipeDia && <EllipseStyled data-testid="weld-ellipsis" />}
          {hasWeldPipeDia && (
            <Text data-testid="weld-pipe-diameter" size="300" color="grey.700">
              {`${weld.pipeDiameter}” ${t`Dia`}`}
            </Text>
          )}
          {weld?.isPriority && (
            <>
              <EllipseStyled data-testid="weld-priority-ellipsis" />
              <PriorityWeldIcon />
            </>
          )}
        </Box>
      </Box>
      {!isUserForeman && weldOwners && weldOwners.length > 0 && (
        <Box data-testid="weld-owners" alignSelf={{ sm: 'end' }} ml={{ sm: '700' }}>
          <WeldAvatarGroup weldAssignees={weldOwners} currentMilestoneType={weld.currentMilestoneType} />
        </Box>
      )}
    </Box>
  );
};
